import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import GooeyNav from './components/layout/GooeyNav';
import Professional from './pages/Professional';
import Blogs from './pages/Blogs';
import BlogPost from './pages/BlogPost';
import Personal from './pages/Personal';
import TechStack from './components/layout/TechStack';
import HangingDecorations from './components/effects/HangingDecorations';
import ClickSpark from './components/effects/ClickSpark';
import BlackBackground from './components/effects/BlackBackground';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import './App.css';

// Hovering the spinning UW sign stops it and peeks the campus photo.
// The photo is portaled to <body> so it can layer above the tech-stack
// marquee without lifting the header (and its nav) with it. Kept as its
// own component so hover state doesn't re-render the rest of the app.
const UwLogoPeek = () => {
  const [active, setActive] = useState(false);

  return (
    <>
      <span
        className="uw-logo-wrapper"
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
      >
        <img src="/images/uw-logo.png" alt="UWaterloo Logo" className="uw-logo" />
      </span>
      {createPortal(
        <img
          src="/images/uw.jpg"
          alt="University of Waterloo"
          className={`uw-peek${active ? ' active' : ''}`}
        />,
        document.body
      )}
    </>
  );
};

function App() {
  // Xeyes mouse tracking effect - clean transform approach
  useEffect(() => {
    const handleMouseMove = (e) => {
      const pupils = document.querySelectorAll('.pupil');
      pupils.forEach(pupil => {
        const eye = pupil.parentElement;
        const eyeRect = eye.getBoundingClientRect();
        
        // Get the center of the eye
        const eyeX = eyeRect.left + eyeRect.width / 2;
        const eyeY = eyeRect.top + eyeRect.height / 2;
        
        // Calculate the angle from eye to mouse
        const angle = Math.atan2(e.clientY - eyeY, e.clientX - eyeX);
        
        // Move the pupil in the direction of the mouse
        const distance = 6; // How far the pupil can move (reduced to stay within boundaries)
        const pupilX = Math.cos(angle) * distance;
        const pupilY = Math.sin(angle) * distance;
        
        // Use transform for smooth movement
        pupil.style.transform = `translate(calc(-50% + ${pupilX}px), calc(-50% + ${pupilY}px))`;
      });
    };

    // Only add mouse tracking on desktop
    if (window.innerWidth > 768) {
      document.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <>
      <Router>
        <ScrollToTop />
        <BlackBackground />
        <ClickSpark
          sparkColor='#fff'
          sparkSize={10}
          sparkRadius={15}
          sparkCount={8}
          duration={400}
        >
          <div className="App">
            <header className="header">
              {/* Desktop layout */}
              <div className="desktop-header">
                <div className="header-left">
                  <div className="logo-container">
                    <img src="/images/julia-logo.png" alt="Julia Logo" className="julia-logo" />
                    <UwLogoPeek />
                    <span className="subtitle">Software Engineering @ UWaterloo</span>
                  </div>
                </div>

                <GooeyNav />

                <div className="header-right">
                  <div className="xeyes-container">
                    <div className="eye">
                      <div className="eyebrow"></div>
                      <div className="pupil"></div>
                    </div>
                    <div className="eye">
                      <div className="eyebrow"></div>
                      <div className="pupil"></div>
                    </div>
                  </div>
                  <div className="motto">Dream it? → Build it.</div>
                  <div className="social-links">
                    <a href="https://www.linkedin.com/in/julia-ilioukhina/" target="_blank" rel="noopener noreferrer">
                      <img src="/images/linkedin.png" alt="LinkedIn" />
                    </a>
                    <a href="https://github.com/juliailioukhina001" target="_blank" rel="noopener noreferrer">
                      <img src="/images/github.png" alt="GitHub" />
                    </a>
                    <a href="https://se-webring.xyz/" target="_blank" rel="noopener noreferrer">
                      <img src="/images/logo_bg_b.png" alt="SE Webring" />
                    </a>
                    <a href="https://x.com/JuliaIlio" target="_blank" rel="noopener noreferrer">
                      <img src="/images/x.png" alt="X" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Mobile layout - two rows */}
              <div className="mobile-header">
                <div className="header-top-row">
                  <div className="mobile-logo-section">
                    <img src="/images/julia-logo.png" alt="Julia Logo" className="mobile-julia-logo" />
                    <span className="mobile-subtitle">Software Engineering @ UWaterloo</span>
                  </div>
                  <div className="social-links">
                    <a href="https://www.linkedin.com/in/julia-ilioukhina/" target="_blank" rel="noopener noreferrer">
                      <img src="/images/linkedin.png" alt="LinkedIn" />
                    </a>
                    <a href="https://github.com/juliailioukhina001" target="_blank" rel="noopener noreferrer">
                      <img src="/images/github.png" alt="GitHub" />
                    </a>
                    <a href="https://se-webring.xyz/" target="_blank" rel="noopener noreferrer">
                      <img src="/images/logo_bg_b.png" alt="SE Webring" />
                    </a>
                    <a href="https://x.com/JuliaIlio" target="_blank" rel="noopener noreferrer">
                      <img src="/images/x.png" alt="X" />
                    </a>
                  </div>
                </div>

                <div className="header-bottom-row">
                  <GooeyNav />
                </div>
                <div className="header-spacer"></div>
              </div>
            </header>
            
            <TechStack />

            <HangingDecorations />
            
            <div className="route-container">
              <Routes>
                <Route path="/" element={<Professional />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blog/:title" element={<BlogPost />} />
                <Route path="/personal" element={<Personal />} />
                
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
            
            <Footer />
          </div>
        </ClickSpark>
      </Router>
    </>
  );
}

export default App;
